/**
 * Human-scale detail concentrated on the rig faces the player can actually see.
 *
 * The primary rig builder owns the platform's architecture. This layer deliberately
 * does not: it adds the smaller working hardware that makes that architecture feel
 * occupied — colour-coded service lines, a valve rack, cable tray, safety mesh,
 * electrical cabinets, a fire point and one readable location plate.
 *
 * Keeping the layer separate also protects the large layout from a detail pass. Every
 * position below sits on an existing deck, catwalk, rail or tower face.
 */

import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { PBRMetallicRoughnessMaterial } from '@babylonjs/core/Materials/PBR/pbrMetallicRoughnessMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { CreateBoxVertexData } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinderVertexData } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreatePlaneVertexData } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { Scene } from '@babylonjs/core/scene';

import { createBeveledBoxVertexData } from './beveledBox';
import { createPhysicalUvs, type RigPrimitiveKind } from './physicalUvs';
import { createRigMaterials } from './rigMaterials';
import { DECK_Y } from './rigBuilder';

const SOUTH_FACE_Z = 19.15;
const STEEL_TILES = 0.15;
const DARK_TILES = 0.13;
const PAINT_TILES = 0.22;

export interface VisibleRigDetailResult {
  meshes: Mesh[];
  triangleCount: number;
}

/**
 * Small accumulator matching the rig's two important surface rules: photographic maps
 * use physical UV scale, and fabricated boxes receive the same real-world chamfer as
 * the large structure.
 */
class DetailBuffer {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly uvs: number[] = [];
  private readonly indices: number[] = [];

  constructor(private readonly tilesPerMetre: number) {}

  add(data: VertexData, transform: Matrix, kind: RigPrimitiveKind, uvScale = 1): void {
    const source = kind === 'box' ? createBeveledBoxVertexData(transform).data : data;
    const base = this.positions.length / 3;
    const positions = source.positions as number[];
    const normals = source.normals as number[];
    const indices = source.indices as number[];
    const normalMatrix = transform.clone().invert().transpose();
    const p = new Vector3();

    for (let i = 0; i < positions.length; i += 3) {
      p.set(
        positions[i] as number,
        positions[i + 1] as number,
        positions[i + 2] as number,
      );
      const transformed = Vector3.TransformCoordinates(p, transform);
      this.positions.push(transformed.x, transformed.y, transformed.z);

      p.set(
        normals[i] as number,
        normals[i + 1] as number,
        normals[i + 2] as number,
      );
      const transformedNormal = Vector3.TransformNormal(p, normalMatrix).normalize();
      this.normals.push(transformedNormal.x, transformedNormal.y, transformedNormal.z);
    }

    this.uvs.push(
      ...createPhysicalUvs(source, transform, kind, this.tilesPerMetre * uvScale),
    );
    for (const index of indices) this.indices.push(base + index);
  }

  box(
    cx: number,
    cy: number,
    cz: number,
    sx: number,
    sy: number,
    sz: number,
    uvScale = 1,
  ): void {
    this.add(
      unitBox(),
      Matrix.Compose(
        new Vector3(sx, sy, sz),
        Quaternion.Identity(),
        new Vector3(cx, cy, cz),
      ),
      'box',
      uvScale,
    );
  }

  tube(a: Vector3, b: Vector3, radius: number, uvScale = 1): void {
    const delta = b.subtract(a);
    const length = delta.length();
    if (length < 1e-5) return;
    const direction = delta.scale(1 / length);
    const up = Vector3.Up();
    const dot = Vector3.Dot(up, direction);
    const axis = Vector3.Cross(up, direction);
    const rotation = axis.lengthSquared() < 1e-8
      ? (dot > 0
        ? Quaternion.Identity()
        : Quaternion.RotationAxis(Vector3.Right(), Math.PI))
      : Quaternion.RotationAxis(
        axis.normalize(),
        Math.acos(Math.min(1, Math.max(-1, dot))),
      );

    this.add(
      unitCylinder(),
      Matrix.Compose(
        new Vector3(radius * 2, length, radius * 2),
        rotation,
        a.add(b).scale(0.5),
      ),
      'cylinder',
      uvScale,
    );
  }

  get triangleCount(): number {
    return this.indices.length / 3;
  }

  build(
    scene: Scene,
    name: string,
    material: PBRMetallicRoughnessMaterial,
  ): Mesh | null {
    if (this.indices.length === 0) return null;
    const mesh = new Mesh(name, scene);
    const data = new VertexData();
    data.positions = this.positions;
    data.normals = this.normals;
    data.uvs = this.uvs;
    data.indices = this.indices;
    data.applyToMesh(mesh, false);
    mesh.material = material;
    mesh.isPickable = false;
    mesh.receiveShadows = true;
    return mesh;
  }
}

let boxData: VertexData | null = null;
let cylinderData: VertexData | null = null;

function unitBox(): VertexData {
  boxData ??= CreateBoxVertexData({ size: 1 });
  return boxData;
}

function unitCylinder(): VertexData {
  cylinderData ??= CreateCylinderVertexData({
    height: 1,
    diameter: 1,
    tessellation: 10,
  });
  return cylinderData;
}

function addPipeRun(
  pipe: DetailBuffer,
  collars: DetailBuffer,
  points: Vector3[],
  radius: number,
): void {
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i] as Vector3;
    const b = points[i + 1] as Vector3;
    pipe.tube(a, b, radius, 7);
    const axis = b.subtract(a).normalize();
    for (const t of [0.055, 0.945]) {
      const centre = Vector3.Lerp(a, b, t);
      const half = axis.scale(radius * 0.34);
      collars.tube(
        centre.subtract(half),
        centre.add(half),
        radius * 1.34,
        5,
      );
    }
  }
}

/** Handwheel facing the player, including its hub, spokes and short valve stem. */
function addValveWheel(
  wheel: DetailBuffer,
  stem: DetailBuffer,
  centre: Vector3,
  radius: number,
): void {
  const segments = 10;
  const point = (angle: number): Vector3 => new Vector3(
    centre.x + Math.cos(angle) * radius,
    centre.y + Math.sin(angle) * radius,
    centre.z,
  );
  for (let i = 0; i < segments; i += 1) {
    wheel.tube(
      point((i / segments) * Math.PI * 2),
      point(((i + 1) / segments) * Math.PI * 2),
      0.035,
      4,
    );
  }
  for (let i = 0; i < 4; i += 1) {
    wheel.tube(centre, point((i / 4) * Math.PI * 2), 0.028, 4);
  }
  stem.tube(
    new Vector3(centre.x, centre.y, centre.z - 0.3),
    new Vector3(centre.x, centre.y, centre.z + 0.18),
    0.075,
    4,
  );
}

/**
 * Wire infill belongs only on the side catwalk. It adds the reference's close safety
 * texture without putting a visual fence across the player's open-ocean firing view.
 */
function addCatwalkSafetyMesh(steel: DetailBuffer): void {
  const x = 10.56;
  const zStart = 7.0;
  const zEnd = 16.0;
  const bottom = DECK_Y + 0.18;
  const top = DECK_Y + 1.02;
  for (let z = zStart; z <= zEnd + 0.01; z += 0.46) {
    steel.tube(
      new Vector3(x, bottom, z),
      new Vector3(x, top, z),
      0.014,
      6,
    );
  }
  for (let y = bottom; y <= top + 0.01; y += 0.28) {
    steel.tube(
      new Vector3(x, y, zStart),
      new Vector3(x, y, zEnd),
      0.014,
      6,
    );
  }
}

function addProcessManifold(
  steel: DetailBuffer,
  dark: DetailBuffer,
  yellow: DetailBuffer,
  blue: DetailBuffer,
  red: DetailBuffer,
): void {
  // Slim support rack, attached to the existing south face rather than creating a new
  // room or changing its silhouette.
  for (const x of [9.8, 14.9, 20.0]) {
    steel.box(x, DECK_Y + 5.25, SOUTH_FACE_Z + 0.24, 0.18, 9.5, 0.22, 5);
  }
  for (const y of [DECK_Y + 0.55, DECK_Y + 5.0, DECK_Y + 9.7]) {
    steel.box(14.9, y, SOUTH_FACE_Z + 0.24, 10.4, 0.2, 0.22, 5);
  }

  addPipeRun(blue, dark, [
    new Vector3(10.6, DECK_Y + 0.5, SOUTH_FACE_Z - 0.02),
    new Vector3(10.6, DECK_Y + 7.55, SOUTH_FACE_Z - 0.02),
    new Vector3(17.2, DECK_Y + 7.55, SOUTH_FACE_Z - 0.02),
    new Vector3(17.2, DECK_Y + 9.3, SOUTH_FACE_Z - 0.02),
  ], 0.16);
  addPipeRun(red, dark, [
    new Vector3(11.5, DECK_Y + 0.5, SOUTH_FACE_Z - 0.2),
    new Vector3(11.5, DECK_Y + 6.15, SOUTH_FACE_Z - 0.2),
    new Vector3(18.5, DECK_Y + 6.15, SOUTH_FACE_Z - 0.2),
  ], 0.135);
  addPipeRun(yellow, dark, [
    new Vector3(12.35, DECK_Y + 0.5, SOUTH_FACE_Z - 0.38),
    new Vector3(12.35, DECK_Y + 4.75, SOUTH_FACE_Z - 0.38),
    new Vector3(19.15, DECK_Y + 4.75, SOUTH_FACE_Z - 0.38),
    new Vector3(19.15, DECK_Y + 8.75, SOUTH_FACE_Z - 0.38),
  ], 0.12);
  addPipeRun(steel, dark, [
    new Vector3(9.7, DECK_Y + 0.5, SOUTH_FACE_Z + 0.16),
    new Vector3(9.7, DECK_Y + 8.85, SOUTH_FACE_Z + 0.16),
    new Vector3(20.0, DECK_Y + 8.85, SOUTH_FACE_Z + 0.16),
  ], 0.22);

  addValveWheel(red, dark, new Vector3(
    14.35,
    DECK_Y + 6.15,
    SOUTH_FACE_Z - 0.43,
  ), 0.38);
  addValveWheel(yellow, dark, new Vector3(
    15.7,
    DECK_Y + 7.55,
    SOUTH_FACE_Z - 0.25,
  ), 0.42);
  addValveWheel(blue, dark, new Vector3(
    16.8,
    DECK_Y + 4.75,
    SOUTH_FACE_Z - 0.61,
  ), 0.32);

  // Electrical termination boxes with raised doors, hinges and colour-coded glands.
  for (const [x, y] of [[19.55, DECK_Y + 2.0], [20.35, DECK_Y + 4.05]] as const) {
    dark.box(x, y, SOUTH_FACE_Z - 0.18, 0.68, 1.25, 0.46, 5);
    steel.box(x, y, SOUTH_FACE_Z - 0.44, 0.56, 1.06, 0.06, 7);
    steel.box(x - 0.2, y, SOUTH_FACE_Z - 0.49, 0.035, 0.78, 0.035, 8);
  }
  red.box(19.69, DECK_Y + 2.18, SOUTH_FACE_Z - 0.5, 0.08, 0.08, 0.055, 8);
  blue.box(20.48, DECK_Y + 4.23, SOUTH_FACE_Z - 0.5, 0.08, 0.08, 0.055, 8);

  // Overhead ladder tray: two side channels and closely spaced cross-rungs.
  for (const z of [SOUTH_FACE_Z - 0.1, SOUTH_FACE_Z + 0.42]) {
    dark.box(14.9, DECK_Y + 10.15, z, 11.2, 0.12, 0.12, 6);
  }
  for (let x = 9.4; x <= 20.4; x += 0.72) {
    dark.box(x, DECK_Y + 10.15, SOUTH_FACE_Z + 0.16, 0.1, 0.1, 0.64, 6);
  }
}

/** Compact two-bottle fire point mounted on the catwalk's outer rail. */
function addFirePoint(
  steel: DetailBuffer,
  dark: DetailBuffer,
  yellow: DetailBuffer,
  red: DetailBuffer,
): void {
  dark.box(10.32, DECK_Y + 1.0, 9.65, 0.16, 1.9, 1.45, 5);
  yellow.box(10.2, DECK_Y + 1.93, 9.65, 0.2, 0.08, 1.5, 7);
  for (const z of [9.35, 9.95]) {
    red.tube(
      new Vector3(10.08, DECK_Y + 0.18, z),
      new Vector3(10.08, DECK_Y + 1.35, z),
      0.18,
      5,
    );
    dark.tube(
      new Vector3(10.08, DECK_Y + 1.35, z),
      new Vector3(10.08, DECK_Y + 1.53, z),
      0.075,
      5,
    );
    steel.tube(
      new Vector3(10.08, DECK_Y + 1.5, z),
      new Vector3(9.94, DECK_Y + 1.63, z),
      0.035,
      6,
    );
  }
  steel.box(10.02, DECK_Y + 0.42, 9.65, 0.08, 0.12, 1.28, 7);
}

function makeUtilityMaterial(
  source: PBRMetallicRoughnessMaterial,
  name: string,
  color: Color3,
): PBRMetallicRoughnessMaterial {
  const material = source.clone(name) as PBRMetallicRoughnessMaterial | null;
  if (!material) throw new Error(`Failed to clone rig material for ${name}`);
  // Paint colour is the signal here. The scan's normal/roughness maps remain, while its
  // grey albedo is removed so blue and red survive the overcast grade.
  material.baseTexture = null;
  material.baseColor = color;
  material.metallic = 0.04;
  material.roughness = 0.76;
  return material;
}

function buildModuleSign(
  scene: Scene,
  source: PBRMetallicRoughnessMaterial,
): Mesh {
  const texture = new DynamicTexture(
    'rigModuleCSignTexture',
    { width: 1024, height: 384 },
    scene,
    false,
  );
  const ctx = texture.getContext() as unknown as CanvasRenderingContext2D;
  ctx.fillStyle = '#e2b600';
  ctx.fillRect(0, 0, 1024, 384);
  ctx.strokeStyle = '#151719';
  ctx.lineWidth = 24;
  ctx.strokeRect(12, 12, 1000, 360);
  ctx.fillStyle = '#151719';
  ctx.font = '900 132px Arial, Helvetica, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('MODULE C', 512, 165);
  ctx.fillRect(48, 198, 928, 6);
  ctx.font = '700 52px Arial, Helvetica, sans-serif';
  ctx.fillText('HIGH PRESSURE  ·  KEEP CLEAR', 512, 286);
  texture.update(false);
  texture.gammaSpace = true;

  const material = source.clone('rigModuleCSignMaterial') as
    | PBRMetallicRoughnessMaterial
    | null;
  if (!material) throw new Error('Failed to clone the module sign material');
  material.baseTexture = texture;
  material.baseColor = Color3.White();
  material.metallic = 0.02;
  material.roughness = 0.84;
  material.backFaceCulling = false;

  const sign = new Mesh('rigModuleCSign', scene);
  CreatePlaneVertexData({
    width: 3.3,
    height: 1.24,
    sideOrientation: Mesh.DOUBLESIDE,
  }).applyToMesh(sign, false);
  // Kept near eye height: signs in the references are useful because a person can
  // actually read them from the walkway, not because they decorate a distant roof.
  sign.position.set(18.35, DECK_Y + 3.35, SOUTH_FACE_Z - 0.48);
  sign.rotation.y = Math.PI;
  sign.rotation.z = Math.PI;
  sign.material = material;
  sign.isPickable = false;
  sign.receiveShadows = true;
  return sign;
}

export function buildVisibleRigDetails(scene: Scene): VisibleRigDetailResult {
  const materials = createRigMaterials(scene);
  const serviceBlue = makeUtilityMaterial(
    materials.steel,
    'rigServiceBlue',
    new Color3(0.025, 0.2, 0.66),
  );
  const fireRed = makeUtilityMaterial(
    materials.steel,
    'rigFireRed',
    new Color3(0.68, 0.035, 0.02),
  );

  const steel = new DetailBuffer(STEEL_TILES);
  const dark = new DetailBuffer(DARK_TILES);
  const yellow = new DetailBuffer(PAINT_TILES);
  const blue = new DetailBuffer(PAINT_TILES);
  const red = new DetailBuffer(PAINT_TILES);

  addProcessManifold(steel, dark, yellow, blue, red);
  addCatwalkSafetyMesh(steel);
  addFirePoint(steel, dark, yellow, red);

  const meshes: Mesh[] = [];
  const built = [
    [steel, 'rigVisibleDetailSteel', materials.steel],
    [dark, 'rigVisibleDetailDark', materials.darkSteel],
    [yellow, 'rigVisibleDetailYellow', materials.yellowPaint],
    [blue, 'rigVisibleDetailBlue', serviceBlue],
    [red, 'rigVisibleDetailRed', fireRed],
  ] as const;
  for (const [buffer, name, material] of built) {
    const mesh = buffer.build(scene, name, material);
    if (mesh) meshes.push(mesh);
  }

  const sign = buildModuleSign(scene, materials.yellowPaint);
  meshes.push(sign);

  return {
    meshes,
    triangleCount: steel.triangleCount + dark.triangleCount + yellow.triangleCount
      + blue.triangleCount + red.triangleCount + sign.getTotalIndices() / 3,
  };
}
